// @storybook-internal widget renderer catalogued through PlacedWidgetItem.
import React, { useState } from "react";
import "./widget-cards.css";

import type { T_ChatV8FeedWidgetData } from "@lattice/core-lib/types/chat-v8";
import type { T_FeedItemResponse } from "@lattice/core-lib/types/feed/feed-item.types";

export type T_FeedWidgetCardProps = {
  data: T_ChatV8FeedWidgetData;
  openSourceUrl?: (url: string) => void;
};

/** Web mirror of the mobile FeedWidgetCard: expandable headlines with
 * source links. Interaction analytics are wired by the transcript layer on
 * web (no direct trpc coupling inside the renderer). */
export function FeedWidgetCard({
  data,
  openSourceUrl = (url) => window.open(url, "_blank", "noopener,noreferrer"),
}: T_FeedWidgetCardProps) {
  const [expandedCuid, setExpandedCuid] = useState<string | null>(null);

  if (data.items.length === 0) {
    return (
      <div className="cw-card">
        <p className="cw-muted">
          {data.emptyReason ?? "No stories for this request right now."}
        </p>
      </div>
    );
  }

  return (
    <div className="cw-card">
      {data.items.map((item) => (
        <FeedItem
          expanded={expandedCuid === item.cuid}
          item={item}
          key={item.cuid}
          onOpenSource={openSourceUrl}
          onToggle={() =>
            setExpandedCuid((current) =>
              current === item.cuid ? null : item.cuid,
            )
          }
        />
      ))}
    </div>
  );
}

function FeedItem({
  expanded,
  item,
  onOpenSource,
  onToggle,
}: {
  expanded: boolean;
  item: T_FeedItemResponse;
  onOpenSource: (url: string) => void;
  onToggle: () => void;
}) {
  return (
    <article className="cw-feed-item">
      <button
        aria-expanded={expanded}
        className="cw-feed-headline"
        onClick={onToggle}
        type="button"
      >
        {item.title}
      </button>
      <p className="cw-feed-meta">
        {[item.primaryTicker, item.category, formatDate(item.publishedAt)]
          .filter(Boolean)
          .join(" · ")}
      </p>
      {expanded ? (
        <>
          {(item.fullDescription ?? item.description) ? (
            <p className="cw-feed-summary">
              {item.fullDescription ?? item.description}
            </p>
          ) : null}
          {item.sourceUrl ? (
            <button
              className="cw-feed-source"
              onClick={() => item.sourceUrl && onOpenSource(item.sourceUrl)}
              type="button"
            >
              Read the source
            </button>
          ) : null}
        </>
      ) : null}
    </article>
  );
}

function formatDate(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
  });
}
